import { Injectable } from 'angular-ts-decorators';

import { Domain } from '@shared/app/scripts/services/domain';
import type { IConfig } from '@shared/app/scripts/services/config';
import type { IStandardErrorParser } from '@shared/app/scripts/services/standard-error-parser';
import { SpynlBase } from '@shared/app/scripts/services/spynl-base';

/**
 * Factory in the coreModule. It takes care of communication with tenant
 * resource. Methods are inherited form {@link shared.spynlBase
 * spynlBase} service, and are documented there.
 */
@Injectable('tenant')
export class Tenant extends SpynlBase<Object> {
  protected location: string;

  constructor(
    protected $http: ng.IHttpService,
    protected $q: ng.IQService,
    protected sentry,
    protected domain: Domain,
    protected config: IConfig,
    protected standardErrorParser: IStandardErrorParser,
  ) {
    'ngInject';

    super($http, $q, sentry, domain, config, standardErrorParser, Object);

    this.location = '/tenants';
  }

  /**
   *
   * Try to run update operation defined in updateOperation.
   * @param {string} tenantId
   * Id of the tenant to be updated
   * @param {object} updateOperation
   * Data to be updated, will become a parameter to the MongoDB $set
   * operator.
   * @returns {promise} it will resolve with success message, or fail with
   * error description
   */
  update(tenantId: string, updateOperation: any): ng.IPromise<any> {
    if (!angular.isString(tenantId)) {
      // TODO make text translatable
      return this.$q.reject('The entity id is undefined, not able to save it');
    }

    return this.$http
      .post(this.getLocation('/tenants/' + tenantId) + '/edit', {
        data: updateOperation,
        // TODO CORE-440 move error to catch
      })

      .catch(this.standardErrorParser)
      .then(
        (response) => response.data.message,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Get the array of owners from the tenant document
   * @param {string} tenantId
   * Id of the tenant. Optional parameter. If not provided the owners of the
   * current user's account will be returned.
   * @returns {promise} it will resolve with success message, or fail with
   * error description
   */
  getOwners(tenantId?: string): ng.IPromise<any> {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return this.$http
      .get(this.baseUrl() + tenantSection + '/tenants/get-owners')
      .catch(this.standardErrorParser)
      .then(
        (response) => response.data.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
       *
       * Makes use of the {@link https://spynl.dev.swcloud.nl/about/endpoints#!/account32management/get_tenants_get_counters get_counters} endpoint
       * Returns a counter object for the given tenant
       * ```
       * {
       *  "posInstanceId": 12510111,
          "salesOrder": 10111223,
          "packingList": 1122,
          "invoice": 112
       * }
       * ```
       * @param {string} tenantId optional Id of the tenant you want to retrieve counters for
       * @returns {promise} it will resolve with success message, or fail with error description
       */
  getCounters(tenantId?: string): ng.IPromise<any> {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return this.$http
      .get(this.baseUrl() + tenantSection + '/tenants/get-counters')
      .catch(this.standardErrorParser)
      .then(
        (response) => response.data.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   *  Update the counters with new values.
   *  The given counters in the object need to have a higher value then the current value.
   *  Makes use of the {@link https://spynl.dev.swcloud.nl/about/endpoints#!/account32management/post_tenants_save_counters save_counters} endpoint*
   * ```
   * {
   *  "posInstanceId": 12510111,
   *  "salesOrder": 10111223,
   *  "packingList": 1122,
   *  "invoice": 112
   * }
   *  ```
   * @param {string} tenantId counters that
   * @param {object} counters counters that need to be updated
   * @returns {promise} it will resolve with success message, or fail with error description
   *
   */
  saveCounters(tenantId: string, counters: any): ng.IPromise<any> {
    let tenantSection = '';
    if (!angular.isObject(counters) || angular.isArray(counters)) {
      // TODO make text translatable
      return this.$q.reject('second parameter should be an object');
    }
    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return this.$http
      .post(this.getLocation(tenantSection) + '/save-counters', counters)
      .catch(this.standardErrorParser)
      .then(
        (response) => response.data.data,
        (error) => this.$q.reject(error.data.message),
      );
  }
}
